import { useEffect, useRef, type ReactNode } from 'react';

/**
 * A native dialog, which brings Escape, focus trapping and the backdrop with it. Clicking outside
 * the panel closes, because the backdrop is the dialog element itself.
 */
export default function Modal({
  open,
  onClose,
  title,
  subtitle,
  headActions,
  className = '',
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  headActions?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) {
      element.showModal();
    } else if (!open && element.open) {
      element.close();
    }
  }, [open]);

  return (
    <dialog
      ref={dialog}
      className={`modal ${className}`}
      onClose={onClose}
      onClick={(event) => {
        if (event.target === dialog.current) onClose();
      }}
    >
      <div className="modal__panel">
        <header className="modal__head">
          <div>
            <h2>{title}</h2>
            {subtitle && <p className="muted small">{subtitle}</p>}
          </div>
          <div className="row">
            {headActions}
            <button type="button" className="button" onClick={onClose}>
              Close
            </button>
          </div>
        </header>
        {children}
      </div>
    </dialog>
  );
}
